/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

import React from 'react';
import _ from 'lodash';
import { Container, Icon } from 'semantic-ui-react';
import { withRouter } from 'react-router-dom';
import { decorate, observable, runInAction, action, computed } from 'mobx';
import { inject, observer } from 'mobx-react';
import { swallowError } from '@amzn/base-ui/dist/helpers/utils';
import { isStoreError, isStoreLoading, isStoreReady } from '@amzn/base-ui/dist/models/BaseStore';
import { createLink } from '@amzn/base-ui/dist/helpers/routing';
import ErrorBox from '@amzn/base-ui/dist/parts/helpers/ErrorBox';
import BasicProgressPlaceholder from '@amzn/base-ui/dist/parts/helpers/BasicProgressPlaceholder';

import ProjectConfigure from './ProjectConfigure';
import Paginate from '../helpers/Paginate';

const iconStyle = { lineHeight: 1, height: '1em', margin: 0 };
const PER_PAGE_DEFAULT = 50;

// Multi-sort helpers (shared pattern with UsersList)
function nextSortState(current, field, isCtrl) {
  const existing = current.findIndex(s => s.field === field);
  if (isCtrl) {
    const next = [...current];
    if (existing >= 0) {
      next[existing] = { field, desc: !next[existing].desc };
    } else {
      next.push({ field, desc: false });
    }
    return next;
  }
  if (current.length === 1 && existing === 0) {
    return [{ field, desc: !current[0].desc }];
  }
  return [{ field, desc: false }];
}

function applySorts(list, sorts, accessors) {
  if (sorts.length === 0) return list;
  const fields = sorts.map(s => (item) => {
    const fn = accessors[s.field];
    return fn ? fn(item) : '';
  });
  const dirs = sorts.map(s => (s.desc ? 'desc' : 'asc'));
  return _.orderBy(list, fields, dirs);
}

class ProjectsList extends React.Component {
  constructor(props) {
    super(props);
    runInAction(() => {
      this.search = '';
      this.sorts = [{ field: 'id', desc: false }];
      this.page = 1;
      this.perPage = PER_PAGE_DEFAULT;
      this.openProjectId = null;
    });
  }

  componentDidMount() {
    const store = this.props.projectsStore;
    swallowError(store.load());
  }

  goto(pathname) {
    const { location, history } = this.props;
    const link = createLink({ location, pathname });
    history.push(link);
  }

  handleAddProject = () => { this.goto('/projects/add'); };

  handleSearchChange = (e) => {
    this.search = e.target.value;
    this.page = 1;
  };

  handleSort = (field, e) => {
    this.sorts = nextSortState(this.sorts, field, e.ctrlKey || e.metaKey);
  };

  handlePageChange = (newPage) => { this.page = newPage; };
  handlePerPageChange = (newPerPage) => { this.perPage = newPerPage; this.page = 1; };

  get sortAccessors() {
    return {
      id: p => (p.id || '').toLowerCase(),
      indexId: p => (p.indexId || '').toLowerCase(),
      description: p => (p.description || '').toLowerCase(),
    };
  }

  get filteredProjects() {
    const store = this.props.projectsStore;
    let projects = store.list;

    if (this.search.trim()) {
      const term = this.search.toLowerCase().trim();
      projects = projects.filter(p => {
        const name = (p.id || '').toLowerCase();
        const desc = (p.description || '').toLowerCase();
        const index = (p.indexId || '').toLowerCase();
        const admins = (p.projectAdminUsers || []).map(u => u.username || '').join(' ').toLowerCase();
        const notes = (p.notes || '').toLowerCase();
        return name.includes(term) || desc.includes(term) || index.includes(term) || admins.includes(term) || notes.includes(term);
      });
    }

    return applySorts(projects, this.sorts, this.sortAccessors);
  }

  render() {
    const store = this.props.projectsStore;
    let content = null;
    if (isStoreError(store)) {
      content = <ErrorBox error={store.error} />;
    } else if (isStoreLoading(store)) {
      content = <BasicProgressPlaceholder segmentCount={3} />;
    } else if (isStoreReady(store)) {
      content = this.renderMain();
    }

    return (
      <Container className="mt3 animated fadeIn">
        {this.renderHeader()}
        {content}
      </Container>
    );
  }

  renderHeader() {
    const store = this.props.projectsStore;
    const count = isStoreReady(store) ? store.list.length : null;

    return (
      <div className="workspace-list-header" style={{ marginBottom: '16px' }}>
        <div className="workspace-list-header-top" style={{ gap: '12px' }}>
          <h3 style={{ margin: 0, fontSize: '1.15em', fontWeight: 600, color: 'var(--rmit-blue)' }}>
            <Icon name="briefcase" style={{ ...iconStyle, marginRight: '8px' }} />
            Projects
          </h3>
          {count !== null && (
            <span className="workspace-status-badge" style={{ background: '#f3f4f6', color: '#374151', fontSize: '0.75em' }}>
              {count} total
            </span>
          )}
        </div>
        <div className="workspace-list-header-actions">
          <button className="workspace-action-btn workspace-action-btn-primary" onClick={this.handleAddProject}>
            <Icon name="plus" style={iconStyle} /> Add Project
          </button>
        </div>
      </div>
    );
  }

  renderMain() {
    const filtered = this.filteredProjects;
    const firstIndex = (this.page - 1) * this.perPage;
    const pageProjects = filtered.slice(firstIndex, firstIndex + this.perPage);

    return (
      <div data-testid="projects-table">
        {this.renderSearch()}
        <div style={{ fontStyle: 'italic', color: '#9ca3af', textAlign: 'right', fontSize: '0.8em', marginBottom: '6px' }}>
          Hold Ctrl and click column headers to sort by multiple columns.
        </div>
        {this.renderTable(pageProjects)}
        <Paginate
          entriesPerPage={this.perPage}
          totalEntries={filtered.length}
          currentPage={this.page}
          onPageChange={this.handlePageChange}
          onPerPageChange={this.handlePerPageChange}
        />
      </div>
    );
  }

  renderSearch() {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
        <div style={{ position: 'relative', width: '100%', maxWidth: '400px' }}>
          <Icon name="search" style={{
            position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)',
            color: '#9ca3af', margin: 0, lineHeight: 1, height: '1em', fontSize: '0.9em', pointerEvents: 'none',
          }} />
          <input
            type="text"
            placeholder="Search by name, description, index, admin, or notes..."
            value={this.search}
            onChange={this.handleSearchChange}
            style={{
              width: '100%', padding: '10px 14px 10px 38px', border: '1px solid #e5e7eb',
              borderRadius: '10px', fontSize: '0.9em', fontFamily: 'inherit', outline: 'none',
              transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
            }}
            onFocus={e => { e.target.style.borderColor = 'var(--rmit-blue-50)'; e.target.style.boxShadow = '0 0 0 2px rgba(0, 0, 84, 0.08)'; }}
            onBlur={e => { e.target.style.borderColor = '#e5e7eb'; e.target.style.boxShadow = 'none'; }}
          />
        </div>
      </div>
    );
  }

  renderSortHeader(label, field) {
    const sortEntry = this.sorts.find(s => s.field === field);
    const sortIndex = this.sorts.length > 1 ? this.sorts.findIndex(s => s.field === field) : -1;
    return (
      <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={(e) => this.handleSort(field, e)}>
        {label}
        {sortEntry && (
          <>
            <Icon
              name={sortEntry.desc ? 'caret down' : 'caret up'}
              style={{ ...iconStyle, marginLeft: '4px', fontSize: '0.8em' }}
            />
            {sortIndex >= 0 && (
              <span style={{ fontSize: '0.65em', color: '#9ca3af', marginLeft: '2px' }}>{sortIndex + 1}</span>
            )}
          </>
        )}
      </th>
    );
  }

  renderTable(pageProjects) {
    if (pageProjects.length === 0) {
      return (
        <div className="workspace-empty-state" style={{ marginTop: '24px' }}>
          <div className="workspace-empty-state-icon"><Icon name="briefcase" /></div>
          <div className="workspace-empty-state-title">No projects found</div>
          <div className="workspace-empty-state-subtitle">
            {this.search ? 'No projects match your search criteria.' : 'No projects have been created yet.'}
          </div>
        </div>
      );
    }

    return (
      <table className="config-table" style={{ marginTop: '8px' }}>
        <thead>
          <tr>
            {this.renderSortHeader('Project Name', 'id')}
            {this.renderSortHeader('Index Id', 'indexId')}
            {this.renderSortHeader('Description', 'description')}
            <th>Project Admins</th>
            <th>Notes</th>
          </tr>
        </thead>
        <tbody>
          {pageProjects.map(project => this.renderProjectRow(project))}
        </tbody>
      </table>
    );
  }

  renderProjectRow(project) {
    const adminUsers = project.projectAdminUsers || [];
    const adminNames = adminUsers.map(u => u.username || '').filter(Boolean);

    return (
      <tr key={project.id} onClick={() => this.handleRowClick(project)} style={{ cursor: 'pointer' }}>
        <td style={{ whiteSpace: 'nowrap' }}><span className="config-name">{project.id}</span></td>
        <td style={{ fontSize: '0.85em', color: '#6b7280' }}>{project.indexId}</td>
        <td style={{ fontSize: '0.85em', color: '#374151' }}>
          {project.description || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>None</span>}
        </td>
        <td style={{ fontSize: '0.85em' }}>
          {adminNames.length > 0
            ? adminNames.join(', ')
            : <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>None</span>
          }
        </td>
        <td style={{ fontSize: '0.85em', color: '#6b7280' }}>
          {project.notes || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>None</span>}
        </td>
        <td style={{ padding: 0, width: 0, border: 'none', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
          <ProjectConfigure
            project={{ ...project }}
            rowClickMode
            isOpen={this.openProjectId === project.id}
            onClose={() => this.handleDetailClose()}
            userStore={this.props.userStore}
            usersStore={this.props.usersStore}
            projectsStore={this.props.projectsStore}
            awsAccountsStore={this.props.awsAccountsStore}
          />
        </td>
      </tr>
    );
  }

  handleRowClick = (project) => {
    this.openProjectId = project.id;
  };

  handleDetailClose = () => {
    this.openProjectId = null;
  };
}

decorate(ProjectsList, {
  search: observable,
  sorts: observable,
  page: observable,
  perPage: observable,
  openProjectId: observable,
  filteredProjects: computed,
  sortAccessors: computed,
  handleAddProject: action,
  handleSearchChange: action,
  handleSort: action,
  handlePageChange: action,
  handlePerPageChange: action,
  handleRowClick: action,
  handleDetailClose: action,
});

export default inject(
  'awsAccountsStore',
  'projectsStore',
  'userStore',
  'usersStore',
)(withRouter(observer(ProjectsList)));
